import type { Device, ThemeClasses } from "../types";
import {
  getStatusColor,
  getStatusLabel,
  getDevicePrefix,
  getDeviceIcon,
} from "../utils/helpers";
import { useRealtimeDeviceData } from "../../../../../../store/admin/vehiclesSocketSelector";
import { useIsReseller } from "../../../../../../hooks/useIsReseller";
import { useTranslation } from "react-i18next";
import {
  getTimeAgo,
  type TimeTranslations,
} from "../../../../../../utils/getDate";
import { useMemo } from "react";
import { DeviceCacheRefreshButton } from "./DeviceCacheRefreshButton";
import { DeviceActionsMenu } from "./DeviceActionsMenu";

interface DeviceGridCardProps {
  device: Device;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  indexNumber: string;
  onEdit: (device: Device) => void;
  onDetails: (device: Device) => void;
  onActivate?: (device: Device) => void;
  onDeactivate?: (device: Device) => void;
  onDelete?: (device: Device) => void;
  onTransfer?: (device: Device) => void;
  t: (key: string, options?: any) => string;
  isSelectionMode?: boolean;
  isSelected?: boolean;
  onToggleSelection?: (device: Device) => void;
}

export function DeviceGridCard({
  device,
  themeClasses,
  isDarkMode,
  indexNumber,
  onEdit,
  onDetails,
  onActivate,
  onDeactivate,
  onDelete,
  onTransfer,
  t,
  isSelectionMode = false,
  isSelected = false,
  onToggleSelection,
}: DeviceGridCardProps) {
  const { t: tTranslate } = useTranslation("lite");
  const { t: tCommon } = useTranslation("common");

  const timeTranslations: TimeTranslations = useMemo(
    () => ({
      noData: tTranslate("time.noData"),
      rightNow: tTranslate("time.rightNow"),
      min: tTranslate("time.min"),
      hour: tTranslate("time.hour"),
      hours: tTranslate("time.hours"),
      day: tTranslate("time.day"),
      days: tTranslate("time.days"),
      month: tTranslate("time.month"),
      months: tTranslate("time.months"),
      and: tTranslate("time.and"),
    }),
    [tTranslate],
  );

  const deviceIcon = getDeviceIcon(device.type);
  const { isReseller } = useIsReseller();
  const realtime = useRealtimeDeviceData(device.id);

  const fuelValue =
    realtime.hasData && realtime.fuel != null
      ? realtime.fuel
      : device.fuelLevel;
  const batteryValue =
    realtime.hasData && realtime.battery != null
      ? realtime.battery
      : device.batteryLevel;
  const hasFuelData = fuelValue != null;
  const hasBatteryData = batteryValue != null;

  const getFuelBarColor = () => {
    if (!hasFuelData) return "bg-gray-400";
    if (fuelValue > 50) return "bg-[#3B82F6]";
    if (fuelValue > 25) return "bg-amber-500";
    return "bg-red-500";
  };

  const getBatteryBarColor = () => {
    if (!hasBatteryData) return "bg-gray-400";
    if (batteryValue > 80) return "bg-[#22C55E]";
    if (batteryValue > 60) return "bg-[#F59E0B]";
    if (batteryValue > 40) return "bg-[#EF4444]";
    return "bg-gray-400";
  };

  const getFuelTextColor = () => {
    if (!hasFuelData) return "text-gray-400";
    return "";
  };

  const getBatteryTextColor = () => {
    if (!hasBatteryData) return "text-gray-400";
    if (batteryValue > 80) return "text-[#22C55E]";
    if (batteryValue > 60) return "text-[#F59E0B]";
    if (batteryValue > 40) return "text-[#EF4444]";
    return "text-gray-400";
  };

  const isCentralGPS = import.meta.env.VITE_THEME === "central-gps";

  const handleCardClick = () => {
    if (isSelectionMode && onToggleSelection) {
      onToggleSelection(device);
    }
  };

  return (
    <div
      className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-5 hover:shadow-lg transition-all ${
        isSelectionMode ? "cursor-pointer" : ""
      } ${isSelected ? "ring-2 ring-primary-500" : ""}`}
      onClick={isSelectionMode ? handleCardClick : undefined}
    >
      {/* Selection Checkbox */}
      {isSelectionMode && (
        <div className="flex items-center mb-3">
          <div
            className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
              isSelected
                ? "bg-primary-500 border-primary-500"
                : isDarkMode
                ? "border-gray-500"
                : "border-gray-300"
            }`}
          >
            {isSelected && <i className="ri-check-line text-white text-sm"></i>}
          </div>
          <span className={`ml-2 text-sm ${themeClasses.textSecondary}`}>
            {isSelected ? t("devices.selected") : t("devices.selectToAction")}
          </span>
        </div>
      )}

      {/* Device Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div
            className={`w-10 h-10 ${deviceIcon.bgColor} ${deviceIcon.textColor} rounded-xl flex items-center justify-center`}
          >
            <i className={`${deviceIcon.icon} text-lg`}></i>
          </div>
          <div>
            <div className="flex items-center gap-1.5">
              <h3 className={`font-semibold ${themeClasses.text}`}>
                {device.network?.model_name || device.model || device.name || device.identifier || "N/A"}
              </h3>
              {isReseller && !isCentralGPS && (
                <DeviceCacheRefreshButton device={device} themeClasses={themeClasses} isDarkMode={isDarkMode} />
              )}
            </div>
            <p className={`text-sm ${themeClasses.textSecondary}`}>
              {`${getDevicePrefix(device.type)}-${indexNumber}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
              device.status,
            )}`}
          >
            {getStatusLabel(device.status, tCommon)}
          </span>
        </div>
      </div>

      {/* Device Details */}
      <div className="space-y-3 mb-4">
        {/* IMEI */}
        <div className="flex items-center justify-between">
          <span className={`text-sm ${themeClasses.textSecondary}`}>
            {t("devices.fields.imei")}:
          </span>
          <span className={`text-sm ${themeClasses.text}`}>
            {device.identifier || device.id}
          </span>
        </div>
        {/* Vehicle Name */}
        <div className="flex items-center justify-between">
          <span className={`text-sm ${themeClasses.textSecondary}`}>
            Unidad Asignada:
          </span>
          <span className={`text-sm ${themeClasses.text}`}>
            {device.vehicle?.name
              ? device.vehicle?.name
              : t("devices.fields.empty")}
          </span>
        </div>
        {/* Brand */}
        <div className="flex items-center justify-between">
          <span className={`text-sm ${themeClasses.textSecondary}`}>
            {t("devices.fields.brandName")}:
          </span>
          <span className={`text-sm ${themeClasses.text}`}>
            {device.network?.brand_name || "N/A"}
          </span>
        </div>
        {/* Mileage */}
        <div className="flex items-center justify-between">
          <span className={`text-sm ${themeClasses.textSecondary}`}>
            {t("devices.fields.mileage")}:
          </span>
          <span className={`text-sm ${themeClasses.text}`}>
            {realtime.hasData && realtime.odometer != null
              ? `${Math.round(realtime.odometer).toLocaleString()} km`
              : device.mileage != null
                ? `${device.mileage} km`
                : "Sin datos"}
          </span>
        </div>
        {/* Hours Motor */}
        <div className="flex items-center justify-between">
          <span className={`text-sm ${themeClasses.textSecondary}`}>
            Motor:
          </span>
          <span className={`text-sm ${themeClasses.text}`}>
            {realtime.hasData && realtime.engineHours != null
              ? `${realtime.engineHours} h`
              : device.hoursMotor != null
                ? `${device.hoursMotor} h`
                : "Sin datos"}
          </span>
        </div>
      </div>

      {/* Fuel and Battery */}
      <div className="space-y-3 mb-4">
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className={`text-xs ${themeClasses.textSecondary}`}>
              <i className="ri-gas-station-line mr-1"></i>
              {t("devices.fields.fuelLevel")}:
            </span>
            <span
              className={`text-xs ${getFuelTextColor() || themeClasses.text}`}
            >
              {hasFuelData ? `${Math.round(fuelValue!)}%` : "Sin datos"}
            </span>
          </div>
          <div
            className={`w-full ${
              isDarkMode ? "bg-gray-600" : "bg-gray-200"
            } rounded-full h-1.5`}
          >
            <div
              className={`${getFuelBarColor()} h-1.5 rounded-full transition-all`}
              style={{
                width: `${hasFuelData ? Math.min(fuelValue!, 100) : 0}%`,
              }}
            ></div>
          </div>
        </div>
      </div>

      <div className="space-y-3 mb-4">
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className={`text-xs ${themeClasses.textSecondary}`}>
              <i className="ri-battery-line mr-1"></i>
              {t("devices.fields.batteryLevel")}
            </span>
            <span
              className={`text-xs ${getBatteryTextColor() || themeClasses.text}`}
            >
              {hasBatteryData ? `${Math.round(batteryValue!)}%` : "Sin datos"}
            </span>
          </div>
          <div
            className={`w-full ${
              isDarkMode ? "bg-gray-600" : "bg-gray-200"
            } rounded-full h-1.5`}
          >
            <div
              className={`${getBatteryBarColor()} h-1.5 rounded-full transition-all`}
              style={{
                width: `${hasBatteryData ? Math.min(batteryValue!, 100) : 0}%`,
              }}
            ></div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-2 mb-3">
        <DeviceActionsMenu
          device={device}
          themeClasses={themeClasses}
          onDetails={onDetails}
          onEdit={onEdit}
          onActivate={onActivate}
          onDeactivate={onDeactivate}
          onDelete={onDelete}
          onTransfer={onTransfer}
          t={t}
          fullWidth
        />
      </div>

      {/* Last Connection */}
      <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
        <p className={`text-xs ${themeClasses.textSecondary}`}>
          {t("devices.lastUpdate")}:{" "}
          {device.updatedAt?._seconds
            ? getTimeAgo(device.updatedAt._seconds, timeTranslations)
            : "N/A"}
        </p>
      </div>
    </div>
  );
}